import { useMemo } from "react";

import { DEFAULT_PAGE_SIZE } from "app/constants";

import type { GridColDef } from "@mui/x-data-grid-pro";

/**
 * v7 centres cell content with line-height, which leaves custom renderers at
 * the top of the row. Spread last so a column can set its own `display`.
 */
function withFlexDisplay(column: GridColDef): GridColDef {
  return column.renderCell || column.type === "actions"
    ? { display: "flex", ...column }
    : column;
}

/**
 * Setup columns, enabling clientside filtering and sorting when the
 * number of total rows is less than the pagination limit
 *
 * @param columns - mui data-grid columns
 * @param totalRows - the total number of rows
 * @returns mui data-grid columns
 */
export function useColumnSetup(
  columns: readonly GridColDef[],
  totalRows: number,
) {
  // Memoizing on the threshold (not the raw count) guarantees the returned
  // array identity only changes when the threshold is crossed — DataGridPro
  // resets user-resized column widths whenever the `columns` prop reference
  // changes. The crossing itself still produces a new reference (the colDefs
  // genuinely differ); XGridTable re-applies user-resized widths for that case.
  const clientside = totalRows < DEFAULT_PAGE_SIZE;

  return useMemo(() => {
    if (clientside) {
      return columns.map(withFlexDisplay);
    }

    return columns.map((column) => ({
      ...withFlexDisplay(column),
      sortable: column.sortable ?? false,
      filterable: false,
    }));
  }, [clientside, columns]);
}
